/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { Eye, Mail, Pencil, Trash2 } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { api } from "../../../services/apiService";
import { Badge } from "../../../components/ops/Badge";
import { CollapsibleRow } from "../../../components/ops/CollapsibleRow";
import { useConfirm } from "../../../components/ops/ConfirmProvider";
import { DataToolbar } from "../../../components/ops/DataToolbar";
import { EmptyState } from "../../../components/ops/EmptyState";
import { Modal } from "../../../components/ops/Modal";
import { PageHeader } from "../../../components/ops/PageHeader";
import { Pagination } from "../../../components/ops/Pagination";
import { useToast } from "../../../components/ops/ToastProvider";
import { usePagedList } from "../../../components/ops/usePagedList";

const statuses = ["new", "contacted", "converted", "archived"];
const statusTone = { new: "accent", contacted: "neutral", converted: "success", archived: "neutral" };
const sourceLabels = { contact: "Contact form", tour_request: "Tour request", field_rental: "Field rental", event_inquiry: "Camps & events", careers: "Careers" };
const emptyForm = { name: "", email: "", phone: "", message: "", source: "contact", status: "new" };

export function AdminLeadsPage() {
  const { accessToken } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");

  const [viewingLead, setViewingLead] = useState(null);
  const [editingLead, setEditingLead] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/leads")
      .then((response) => setLeads(response.data.leads || []))
      .catch(() => toast.error("Unable to load leads"))
      .finally(() => setLoading(false));
  }, [accessToken]);

  const filteredLeads = useMemo(() => {
    return leads.filter((lead) => {
      if (statusFilter !== "all" && lead.status !== statusFilter) return false;
      if (sourceFilter !== "all" && lead.source !== sourceFilter) return false;
      return true;
    });
  }, [leads, statusFilter, sourceFilter]);

  const { page, setPage, totalPages, totalItems, pageItems } = usePagedList(filteredLeads, {
    search,
    searchFields: ["name", "email", "phone", "message"],
    pageSize: 10,
  });

  const changeStatus = async (lead, status) => {
    try {
      const response = await api.patch(`/leads/${lead._id}/status`, { status });
      setLeads(leads.map((l) => (l._id === response.data.lead._id ? response.data.lead : l)));
      toast.success("Lead updated.");
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to update lead");
    }
  };

  const openEdit = (lead) => {
    setEditingLead(lead);
    setForm({
      name: lead.name,
      email: lead.email,
      phone: lead.phone || "",
      message: lead.message || "",
      source: lead.source,
      status: lead.status,
    });
  };

  const closeEdit = () => {
    setEditingLead(null);
    setForm(emptyForm);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await api.patch(`/leads/${editingLead._id}`, form);
      setLeads(leads.map((l) => (l._id === response.data.lead._id ? response.data.lead : l)));
      toast.success("Lead updated successfully!");
      closeEdit();
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to update lead");
    } finally {
      setSaving(false);
    }
  };

  const removeLead = async (lead) => {
    const ok = await confirm({
      title: "Delete this lead?",
      message: `The lead from "${lead.name}" will be permanently removed. This can't be undone.`,
      confirmLabel: "Delete lead",
      tone: "danger",
    });
    if (!ok) return;

    try {
      await api.delete(`/leads/${lead._id}`);
      setLeads(leads.filter((l) => l._id !== lead._id));
      toast.success("Lead deleted.");
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to delete lead");
    }
  };

  return (
    <div>
      <PageHeader title="Leads" subtitle="Tour requests and contact form submissions" />

      <div className="ops-card">
        <DataToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by name, email, or message..."
          filters={[
            {
              label: "Status",
              value: statusFilter,
              onChange: setStatusFilter,
              options: [{ value: "all", label: "All statuses" }, ...statuses.map((status) => ({ value: status, label: status }))],
            },
            {
              label: "Source",
              value: sourceFilter,
              onChange: setSourceFilter,
              options: [{ value: "all", label: "All sources" }, ...Object.entries(sourceLabels).map(([value, label]) => ({ value, label }))],
            },
          ]}
          resultCount={totalItems}
          itemLabel="leads"
        />
        {loading ? (
          <p className="ops-loading">Loading leads…</p>
        ) : totalItems === 0 ? (
          <EmptyState icon={Mail} title="No leads found" description={leads.length === 0 ? "Tour requests and contact submissions will appear here." : "Try adjusting your search or filters."} />
        ) : (
          <>
            <ul className="ops-list">
              {pageItems.map((lead) => (
                <CollapsibleRow
                  key={lead._id}
                  avatar={lead.name?.charAt(0)}
                  title={<>{lead.name} <Badge tone="neutral">{sourceLabels[lead.source] || lead.source}</Badge></>}
                  meta={`${lead.email} ${lead.phone ? `· ${lead.phone}` : ""} · ${new Date(lead.createdAt).toLocaleDateString()}`}
                  trailing={
                    <>
                      <Badge tone={statusTone[lead.status] || "neutral"}>{lead.status}</Badge>
                      <select
                        className="ops-select"
                        style={{ width: "auto", padding: "6px 10px" }}
                        value={lead.status}
                        onChange={(e) => changeStatus(lead, e.target.value)}
                      >
                        {statuses.map((status) => <option key={status} value={status}>{status}</option>)}
                      </select>
                      <button type="button" className="ops-button ops-button--outline ops-button--small" onClick={() => setViewingLead(lead)} aria-label="View lead">
                        <Eye size={13} />
                      </button>
                      <button type="button" className="ops-button ops-button--outline ops-button--small" onClick={() => openEdit(lead)} aria-label="Edit lead">
                        <Pencil size={13} />
                      </button>
                      <button type="button" className="ops-button ops-button--danger ops-button--small" onClick={() => removeLead(lead)} aria-label="Delete lead">
                        <Trash2 size={13} />
                      </button>
                    </>
                  }
                >
                  {lead.message ? <p>&ldquo;{lead.message}&rdquo;</p> : <p>No message was submitted with this lead.</p>}
                </CollapsibleRow>
              ))}
            </ul>
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
          </>
        )}
      </div>

      <Modal open={Boolean(viewingLead)} onClose={() => setViewingLead(null)} title="Lead Details" size="sm">
        {viewingLead && (
          <div className="ops-form">
            <div className="ops-form-section">
              <p className="ops-form-section-title">Contact</p>
              <p style={{ margin: "0 0 8px" }}><strong>Name:</strong> {viewingLead.name}</p>
              <p style={{ margin: "0 0 8px" }}><strong>Email:</strong> {viewingLead.email}</p>
              <p style={{ margin: "0 0 8px" }}><strong>Phone:</strong> {viewingLead.phone || "—"}</p>
              <p style={{ margin: 0 }}><strong>Source:</strong> {sourceLabels[viewingLead.source] || viewingLead.source}</p>
            </div>
            <div className="ops-form-section">
              <p className="ops-form-section-title">Message</p>
              <p style={{ margin: 0, color: "var(--ops-muted)" }}>{viewingLead.message || "No message was submitted with this lead."}</p>
            </div>
            <div className="ops-form-section" style={{ borderBottom: "none", paddingBottom: 0, marginBottom: 0 }}>
              <p className="ops-form-section-title">Status</p>
              <p style={{ margin: 0 }}><Badge tone={statusTone[viewingLead.status] || "neutral"}>{viewingLead.status}</Badge> · Submitted {new Date(viewingLead.createdAt).toLocaleString()}</p>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={Boolean(editingLead)}
        onClose={closeEdit}
        title="Edit Lead"
        size="md"
        footer={
          <>
            <button type="submit" form="edit-lead-form" className="ops-button ops-button--primary" disabled={saving}>{saving ? "Saving..." : "Save Changes"}</button>
            <button type="button" className="ops-button ops-button--secondary" onClick={closeEdit}>Cancel</button>
          </>
        }
      >
        <form id="edit-lead-form" onSubmit={submitEdit} className="ops-form">
          <div className="ops-form-section">
            <p className="ops-form-section-title">Contact</p>
            <div className="ops-form-row">
              <div>
                <label className="ops-form-label">Name</label>
                <input required className="ops-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <label className="ops-form-label">Email</label>
                <input required type="email" className="ops-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
            </div>
            <div className="ops-form-group">
              <label className="ops-form-label">Phone <span className="ops-form-label-optional">(optional)</span></label>
              <input type="tel" className="ops-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div className="ops-form-group">
              <label className="ops-form-label">Message</label>
              <textarea className="ops-textarea" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            </div>
          </div>

          <div className="ops-form-section" style={{ borderBottom: "none", paddingBottom: 0, marginBottom: 0 }}>
            <p className="ops-form-section-title">Status</p>
            <div className="ops-form-row">
              <div>
                <label className="ops-form-label">Source</label>
                <select className="ops-select" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
                  {Object.entries(sourceLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </div>
              <div>
                <label className="ops-form-label">Status</label>
                <select className="ops-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  {statuses.map((status) => <option key={status} value={status}>{status}</option>)}
                </select>
              </div>
            </div>
          </div>

        </form>
      </Modal>
    </div>
  );
}
